import React, { useContext, useEffect, useRef, useState } from 'react';

import { AppContext } from '~/contexts/AppContext';

import styled from 'styled-components';
import { animated } from 'react-spring';
import ResultSlide from './ResultSlide';
import { GroupResult } from '~/types';
import { SliderAnimationState } from '../SliderPanel';
import StaticSlider from '~/components/StaticSlider';
import AgeResultSlide from './slides/AgeResultSlide';

const RatingResultStyle = styled(animated.div)`
    width: calc(100% - 60px);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin: 20px auto 0 auto;
`;

const ResultContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

interface RatingResultProps {
    ratingValue: number;
    ratingId: number | null;
    sliderAnimationState: SliderAnimationState;
    onAnimationComplete: () => void;
    loadingResults: { [groupString: string]: boolean };
    groupResults: { [groupString: string]: GroupResult };
    setReferenceRating: (referenceRating: number | null, referenceColor: string | null) => void;
    submitBirthYear: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    isFirstSlide?: boolean;
    shouldBeHidden: boolean;
    onCardVisible?: () => void;
}

interface RatingResultState {
    fadeInComplete: boolean;
    height: number | string;
    opacity: number;
    zIndex: number;
}

const AgeResult: React.FC<RatingResultProps> = (props) => {

    const { topicData } = useContext(AppContext)!;

    const [state, setState] = useState<RatingResultState>(() => ({
        fadeInComplete: false,
        height: props.shouldBeHidden ? 0 : 'auto',
        opacity: 0,
        zIndex: props.shouldBeHidden ? -1 : 1
    }));

    const containerRef = useRef<HTMLDivElement>(null);
    const resultContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (props.shouldBeHidden) {
            const scrollHeight = resultContainerRef.current?.clientHeight ?? 0 + 40;
            requestAnimationFrame(() => setState(prev => ({...prev, height: scrollHeight ?? 0 })));
            containerRef.current?.addEventListener('transitionend', () => {
                setState({...state, height: 'auto', opacity: 1, zIndex: 1, fadeInComplete: true });
            }, { once: true });
        } else {
            requestAnimationFrame(() => setState(prev => ({ ...prev, opacity: 1 })));
            containerRef.current?.addEventListener('transitionend', (e) => {
                if (e.propertyName === 'opacity') {
                    setState(prev => ({ ...prev, fadeInComplete: true }));
                }
            }, { once: true });
        }
    }, [props.shouldBeHidden]);

    useEffect(() => {
        if (props.isFirstSlide) {
            const groupResult: GroupResult | undefined = props.groupResults['age'];

            if (groupResult)
                props.setReferenceRating(groupResult.rating, groupResult.color);
            else
                props.setReferenceRating(null, null);
        }
    }, [props.groupResults, props.isFirstSlide]);

    useEffect(() => {
        if (state.fadeInComplete && props.onCardVisible) {
            props.onCardVisible();
        }
    }, [state.fadeInComplete]);

    return (
        <RatingResultStyle 
            ref={containerRef}
            style={{
                opacity: state.opacity,
                height: state.height,
                transition: 'opacity 0.34s ease, height 0.67s ease',
                zIndex: state.zIndex,
                position: 'relative'
            }}
        >
            <div style={{ 
                filter: (!props.groupResults.age || !props.groupResults.age.rating) ? 'saturate(0)' : 'none',
                transition: 'filter 0.2s ease'
            }}>
                <StaticSlider 
                    userRating={props.ratingValue}
                    majorityRating={props.groupResults.age?.rating || null}
                    showReferenceLine={true}
                    referenceLineColor={props.groupResults.age?.color || '#000000'}
                    hideSlider={props.isFirstSlide}
                />
            </div>
            <ResultContainer ref={resultContainerRef}>
                {topicData?.topic.pvtShowAgeStats && (
                    <div style={{
                        width: '100%'
                    }}>
                        <ResultSlide 
                            index={0} 
                            loadingResults={props.loadingResults.age}
                            groupResult={props.groupResults.age}
                            currentSlide={0} 
                            slideClicked={() => {}}
                        >
                            <AgeResultSlide
                                ratingValue={props.ratingValue}
                                groupResult={props.groupResults.age}
                                submitBirthYear={props.submitBirthYear}
                                fadeInComplete={state.fadeInComplete}
                                onAnimationComplete={props.onAnimationComplete}
                            />
                        </ResultSlide>
                    </div>
                )}
            </ResultContainer>
        </RatingResultStyle>
    );
};

export default AgeResult;